import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  Stack,
  CircularProgress,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  InputAdornment,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  AUDIENCE_SEGMENT_LIST_ENDPOINT,
  AUDIENCE_COUNT_ENDPOINT,
  AUDIENCE_SEGMENT_DELETE_ENDPOINT,
} from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import { Add, Delete, Edit, MoreVert, Search } from "@mui/icons-material";
import AudienceFilterChip from "../Audience/AudienceFilterChip";
import { ColorsConstants } from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import ButtonOne from "../../common/normal-button";
import AppTable from "../../common/app-table";
import TableSearchInput from "../TableSearchInput";
import { formatWithTzLabel } from "../../utils/dateUtils";
import {
  resolveSearchQueryForApi,
  normalizeCombinedSearchInput,
} from "../../utils/phoneSearch";

const Segments = ({ onNavigate }) => {
  const permissionsFromStorage = JSON.parse(
    localStorage.getItem("permissions") || "[]",
  );
  const hasCreatePermission = permissionsFromStorage.includes("segment.create");
  const hasEditPermission   = permissionsFromStorage.includes("segment.edit");
  const hasDeletePermission = permissionsFromStorage.includes("segment.delete");

  const [segments, setSegments]           = useState([]);
  const [page, setPage]                   = useState(0);
  const [rowsPerPage, setRowsPerPage]     = useState(20);
  const [totalCount, setTotalCount]       = useState(0);
  const [loading, setLoading]             = useState(false);
  const [countLoading, setCountLoading]   = useState(null);
  const [audienceCounts, setAudienceCounts] = useState({});
  const [searchQuery, setSearchQuery]     = useState("");
  const [charCount, setCharCount]         = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl]           = useState(null);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const [filtersDialogOpen, setFiltersDialogOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);

  const { getValidToken } = useAuth();

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchData = async (page, rowsPerPage, searchQueryArg = "") => {
    const resolved = resolveSearchQueryForApi(searchQueryArg);
    if (resolved.blockFetch) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const url = `${AUDIENCE_SEGMENT_LIST_ENDPOINT}?limit=${rowsPerPage}&offset=${
      page * rowsPerPage
    }&search_query=${encodeURIComponent(resolved.apiSearchQuery)}`;

    const token  = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    try {
      const response = await fetch(url, { headers });
      if (!response.ok) throw new Error("Failed to fetch data");
      const data = await response.json();
      setSegments(data.results);
      setTotalCount(data.count || 0);
    } catch (error) {
      console.error("Error fetching audience segments:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAudienceCount = async (segment) => {
    setCountLoading(segment.id);
    const token  = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    let queryParams = new URLSearchParams({ limit: 1, offset: 0 });
    segment.filters.forEach(({ field, operator, value }) => {
      field    = field.toLowerCase().replaceAll(" ", "_");
      operator = operator.toLowerCase().replaceAll(" ", "_");
      queryParams.append(field, `${operator}:${value ?? ""}`);
    });

    try {
      const response = await fetch(`${AUDIENCE_COUNT_ENDPOINT}?${queryParams}`, { headers });
      if (!response.ok) throw new Error("Failed to fetch data");
      const data = await response.json();
      setAudienceCounts((prev) => ({ ...prev, [segment.id]: data }));
    } catch (error) {
      console.error("Error fetching audience count:", error);
    } finally {
      setCountLoading(null);
    }
  };

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleChangePage        = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (value) => {
    const normalized = normalizeCombinedSearchInput(value);
    if (normalized.length > 50) return;
    setSearchQuery(normalized);
    setCharCount(normalized.length);
    setPage(0);
  };

  const resolvedSegmentSearch = useMemo(
    () => resolveSearchQueryForApi(searchQuery),
    [searchQuery],
  );

  const handleMenuOpen  = (event, segment) => { setAnchorEl(event.currentTarget); setSelectedSegment(segment); };
  const handleMenuClose = () => { setAnchorEl(null); setSelectedSegment(null); };

  const handleDeleteClick   = () => setDeleteDialogOpen(true);
  const handleDeleteCancel  = () => { setDeleteDialogOpen(false); handleMenuClose(); };

  const handleDeleteConfirm = async () => {
    if (!selectedSegment) return;
    const token  = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    try {
      const res = await fetch(`${AUDIENCE_SEGMENT_DELETE_ENDPOINT}${selectedSegment.id}/`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
      });
      if (!res.ok) throw new Error("Failed to delete segment");
      setSegments((prev) => prev.filter((s) => s.id !== selectedSegment.id));
      setDeleteDialogOpen(false);
    } catch (err) {
      console.error(err);
      setDeleteDialogOpen(false);
    } finally {
      handleMenuClose();
    }
  };

  const handleFiltersClick       = (filters) => { setActiveFilters(filters); setFiltersDialogOpen(true); };
  const handleFiltersDialogClose = () => { setFiltersDialogOpen(false); setActiveFilters([]); };

  useEffect(() => {
    fetchData(page, rowsPerPage, searchQuery);
  }, [page, rowsPerPage, searchQuery]);

  // ── Column definitions ─────────────────────────────────────────────────────

  const tableColumns = useMemo(() => {
    const cols = [
      {
        id: "name",
        label: "Segment Name",
        minWidth: 200,
        render: (segment) => (
          <Typography
            sx={{
              ...typography.bodyLarge,
              cursor: hasEditPermission ? "pointer" : "default",
              width: "fit-content",
              fontWeight: 600,
              color: hasEditPermission ? ColorsConstants.primaryBlue100 : "#1D2330",
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (hasEditPermission) onNavigate("update_segment", { segmentId: segment.id });
            }}
          >
            {segment.name}
          </Typography>
        ),
      },
      {
        id: "filters",
        label: "Filters",
        render: (segment) => (
          <Stack direction="row" gap={1} flexWrap="wrap">
            {segment.filters.length > 0 && (
              <>
                <AudienceFilterChip filter={segment.filters[0]} />
                {segment.filters.length > 1 && (
                  <Typography
                    sx={{ 
                      ...typography.bodySmall, 
                      cursor: "pointer", 
                      color: ColorsConstants.primaryBlue100 
                    }}
                    onClick={(e) => { e.stopPropagation(); handleFiltersClick(segment.filters); }}
                  >
                    +{segment.filters.length - 1} more
                  </Typography>
                )}
              </>
            )}
          </Stack>
        ),
      },
      {
        id: "total_contacts",
        label: "Total Contacts",
        align: "center",
        render: (segment) =>
          audienceCounts[segment.id] !== undefined ? (
            <Typography sx={{ ...typography.bodyLarge, fontWeight: 500 }}>
              {audienceCounts[segment.id]}
            </Typography>
          ) : countLoading === segment.id ? (
            <CircularProgress size={20} />
          ) : (
            <Typography
              sx={{
                ...typography.bodySmall,
                color: ColorsConstants.primaryBlue100,
                cursor: "pointer",
                fontWeight: 600,
              }}
              onClick={(e) => { e.stopPropagation(); fetchAudienceCount(segment); }}
            >
              View Count
            </Typography>
          ),
      },
      {
        id: "created_by_name",
        label: "Created By",
        render: (s) => (
          <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
            {s.created_by_name || "N/A"}
          </Typography>
        ),
      },
      { 
        id: "created_at",   
        label: "Created At",
        render: (s) => (
          <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
            {formatWithTzLabel(s.created_at_iso)}
          </Typography>
        ),
      },
      {
        id: "last_updated",
        label: "Last Updated",
        render: (s) => (
          <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
            {formatWithTzLabel(s.last_updated_iso)}
          </Typography>
        ),
      },
      // Actions column — only included when the user has at least one permission
      ...(hasEditPermission || hasDeletePermission
        ? [
            {
              id: "__actions__",
              label: "Actions",
              align: "center",
              render: (segment) => (
                <IconButton
  size="small"
  onClick={(e) => handleMenuOpen(e, segment)}
  disableRipple
  sx={{
    p: 0.5,
    color: "#111827", // default dark
    "&:hover": {
      backgroundColor: "transparent",
    },
  }}
>
  <MoreVert sx={{ fontSize: 18, color: "inherit" }} />
</IconButton>
              ),
            },
          ]
        : []),
    ];
    return cols;
  }, [hasEditPermission, hasDeletePermission, audienceCounts, countLoading]);

  const rowSx = (row, index) => ({
    backgroundColor: index % 2 === 0 ? "#F0F5FF" : "#FFFFFF",
    "&:hover": {
      backgroundColor: index % 2 === 0 ? "#F5F5F5" : "#F7F9FC",
    },
  });

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3, bgcolor: "#FAFAFA" }}>

      {/* Search + Create bar */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, width: "100%" }}>
        <Box sx={{ flex: 1 }}>
          <TableSearchInput
            searchQuery={searchQuery}
            onSearchChange={(e) => handleSearchChange(e.target.value)}
            charCount={charCount}
            tooltip="Search by Segment Name"
            placeholder="Search segments by name..."
            error={Boolean(resolvedSegmentSearch.blockFetch && resolvedSegmentSearch.phoneSearchError)}
            helperText={resolvedSegmentSearch.blockFetch ? resolvedSegmentSearch.phoneSearchError : undefined}
          />
        </Box>

        {hasCreatePermission && (
          <Box sx={{ flex: "0 0 auto" }}>
            <ButtonOne
              variant="contained"
              startIcon={<Add />}
              onClick={() => onNavigate("create_segment")}
              title="Create Segment"
              sx={{
                backgroundColor: ColorsConstants.primaryBlue100,
                height: "40px",
                whiteSpace: "nowrap",
                px: 3,
                ...typography.bodyLarge,
                "&:hover": { backgroundColor: ColorsConstants.primaryBlue100, opacity: 0.9 },
              }}
            >
              Create Segment
            </ButtonOne>
          </Box>
        )}
      </Box>

      {/* ── Table ── */}
      <AppTable
        columns={tableColumns}
        rows={segments}
        rowKey="id"
        loading={loading}
        emptyMessage="No Segments Found"
        maxHeight={540}
        containerSx={{
          scrollbarWidth: "thin",
          scrollbarColor: "#C5CEDB #F3F6FB",
          "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#F3F6FB",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#C5CEDB",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#A8B4C7",
          },
        }}
        onRowClick={
          hasEditPermission
            ? (segment) => onNavigate("update_segment", { segmentId: segment.id })
            : undefined
        }
        rowClickable={hasEditPermission}
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowSx={rowSx}
      />

     {/* Actions menu */}
{(hasEditPermission || hasDeletePermission) && (
  <Menu
    anchorEl={anchorEl}
    open={menuOpen}
    onClose={handleMenuClose}
    anchorOrigin={{ vertical: "top", horizontal: "left" }}
    transformOrigin={{ vertical: "top", horizontal: "right" }}
    PaperProps={{
      elevation: 0,
      sx: {
        border: "1px solid #DCDFE5",
        borderRadius: "8px",
        p: 0.5,
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      {hasEditPermission && (
        <IconButton
          disableRipple
          onClick={() => {
            handleMenuClose();
            onNavigate("update_segment", {
              segmentId: selectedSegment?.id,
            });
          }}
          sx={{
            p: 0.5,
            color: "#6C7C93",
            "&:hover": {
              backgroundColor: "transparent",
              color: "#111827",
            },
          }}
        >
          <Edit sx={{ fontSize: 18 }} />
        </IconButton>
      )}

      {hasDeletePermission && (
        <IconButton
          disableRipple
          onClick={handleDeleteClick}
          sx={{
            p: 0.5,
            color: "#E74C3C",
            "&:hover": {
              backgroundColor: "transparent",
              color: "#C0392B",
            },
          }}
        >
          <Delete sx={{ fontSize: 18 }} />
        </IconButton>
      )}
    </Box>
  </Menu>
)}

      {/* Delete confirmation dialog */}
      {hasDeletePermission && (
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          maxWidth="sm"
          fullWidth
          PaperProps={{ 
            elevation: 0, 
            sx: { 
              borderRadius: "12px", 
              border: "1px solid #DCDFE5" 
            } 
          }}
        >
          <DialogTitle sx={{ ...typography.h2 }}>Delete Segment</DialogTitle>
          <DialogContent>
            <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>
              Are you sure you want to delete the segment "{selectedSegment?.name}"?
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 1 }}>
            <Button
              onClick={handleDeleteCancel}
              sx={{ 
                textTransform: "none", 
                ...typography.bodyLarge, 
                color: "#6C7C93" 
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              sx={{
                textTransform: "none",
                ...typography.bodyLarge,
                backgroundColor: "#E74C3C",
                "&:hover": { backgroundColor: "#C0392B" },
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Filters dialog */}
      <Dialog
        open={filtersDialogOpen}
        onClose={handleFiltersDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ 
          elevation: 0, 
          sx: { 
            borderRadius: "12px", 
            border: "1px solid #DCDFE5" 
          } 
        }}
      >
        <DialogTitle sx={{ ...typography.h2 }}>Filters Applied</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 1 }}>
            {activeFilters.map((filter, index) => (
              <AudienceFilterChip key={index} filter={filter} />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={handleFiltersDialogClose}
            sx={{ 
              textTransform: "none", 
              ...typography.bodyLarge, 
              color: "#6C7C93" 
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Segments;